import { format, parseISO } from 'date-fns';
import { startCase } from 'lodash';

import {
  RosterRequestDataNewReservation,
  RosterRequestDataNewReservationClientTeamMember,
  useCheckForDuplicateNewReservationRequestsQuery,
} from 'generated/graphql';
import { useAppSelector } from 'store/store';
import getEnumLabel from 'utilities/getEnumLabel';
import { pluralize } from 'utilities/pluralize';

import DuplicateChangeRequestWarning from '../DuplicateChangeRequestWarning';
import OverlappingStayWarning from '../OverlappingStayWarning';

type NewReservationsListProps = {
  requestData: RosterRequestDataNewReservation;
  clientId: string;
  id: string;
  controlNumber?: string;
};

type NewReservationsListItemProps = {
  index: number;
  assignment: RosterRequestDataNewReservationClientTeamMember;
  controlNumber?: string | null;
  type: string;
  preferredHotelName?: string | null;
  clientId?: string | null;
  requestId?: string;
};

const NewReservationsListItem = ({
  index,
  assignment,
}: NewReservationsListItemProps) => {
  const { genders } = useAppSelector((state) => state.enums);

  return (
    <div key={assignment.id}>
      <div className="mb-5 grid grid-cols-12 rounded-md border bg-white p-5">
        <div className="col-span-12 mb-5 border-b border-b-gray-200 pb-[10px]">
          <div className="flex flex-col justify-start">
            <div className="mb-[10px] flex-1">
              <span className="text-base">
                {`${index + 1}. ${assignment.lastName}, ${
                  assignment.firstName
                } (${assignment.edipi})`}
              </span>
            </div>
            <div className="flex-1">
              <div className="mb-[10px] text-sm">
                Gender:{' '}
                {assignment?.gender
                  ? getEnumLabel(genders, assignment.gender)
                  : ''}
              </div>
              <div className="mb-[10px] text-sm">Rank: {assignment.rank}</div>
              <div className="mb-[10px] text-sm">
                Room Type: {assignment.roomType}
              </div>
            </div>
            <div className="flex-1">
              <span className="text-sm">
                {`Check In: ${format(
                  parseISO(assignment.checkin ?? ''),
                  'M/d/yyyy',
                )} →${' '}
                        Check Out: ${format(
                          parseISO(assignment.checkout ?? ''),
                          'M/d/yyyy',
                        )}`}
              </span>
            </div>
            <OverlappingStayWarning
              className="my-[10px]"
              newStayInfo={{
                guestId: assignment?.id,
                checkInDate: assignment?.checkin,
                checkOutDate: assignment?.checkout,
              }}
            />
          </div>
        </div>
        {assignment.remarks && (
          <div className="col-span-12 mb-5">
            <h2 className="mb-[10px] text-base text-primaryGray">Remarks</h2>
            <div className="text-sm">{assignment.remarks}</div>
          </div>
        )}
        {(assignment.previousHotelName || assignment.previousHotelAddress) && (
          <div className="col-span-12 mb-5">
            <h2 className="mb-[10px] text-base text-primaryGray">
              Previous Hotel Information
            </h2>
            <div className="mb-[10px] text-sm">
              Hotel Name:{' '}
              {startCase(assignment.previousHotelName?.toLowerCase())}
            </div>
            <div className="mb-[10px] text-sm">
              Address: {assignment.previousHotelAddress}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const NewReservationsList = ({
  requestData,
  id,
  clientId,
  controlNumber,
}: NewReservationsListProps) => {
  const newGuestCount = requestData?.newGuests?.length;
  const existingGuestCount = requestData?.existingGuests?.length;

  const totalGuests = newGuestCount + existingGuestCount;

  const guestIds = (requestData?.existingGuests
    .map((guest) => guest.id)
    .filter((id) => id != null) ?? []) as string[];

  const { data: duplicateRequestsData, error: duplicateRequestsError } =
    useCheckForDuplicateNewReservationRequestsQuery({
      variables: {
        guestIds: guestIds,
      },
      skip: !guestIds || guestIds.length < 1,
    });

  const duplicateRequests =
    duplicateRequestsData?.duplicateRosterNewReservationRequests?.filter(
      (r) => r.id !== id,
    ) ?? [];

  if (totalGuests === 0) {
    return null;
  }

  return (
    <div>
      <h2 className="mb-5 text-lg text-primaryGray">
        {totalGuests} New Roster {totalGuests === 1 ? 'Entry' : 'Entries'}{' '}
        Requested
      </h2>
      <h2 className="mb-5 text-sm text-primaryGray">
        {newGuestCount} New {pluralize(newGuestCount, 'Guest')}
      </h2>
      {requestData?.newGuests?.map((assignment, index) => (
        <NewReservationsListItem
          key={index}
          index={index}
          assignment={assignment}
          preferredHotelName={requestData.preferredHotelName}
          clientId={clientId}
          requestId={id}
          type="newGuests"
          controlNumber={controlNumber}
        />
      ))}
      <h2 className="mb-5 text-sm text-primaryGray">
        {existingGuestCount} Existing {pluralize(newGuestCount, 'Guest')}
      </h2>
      {(duplicateRequests.length > 0 || duplicateRequestsError) && (
        <div className="mb-4">
          <DuplicateChangeRequestWarning
            duplicateRequests={duplicateRequests}
            error={duplicateRequestsError}
            type="guests"
          />
        </div>
      )}
      {requestData?.existingGuests?.map((assignment, index) => (
        <NewReservationsListItem
          key={index}
          index={index}
          assignment={assignment}
          type="existingGuests"
          preferredHotelName={requestData.preferredHotelName}
          clientId={clientId}
          requestId={id}
          controlNumber={controlNumber}
        />
      ))}
    </div>
  );
};

export default NewReservationsList;
